import { constants } from '@config';

import { logError } from '../../../src/utils/logger';

import { getDataURL, isDataURL } from './dataUrl';

const QUICK_IMAGE_VERSION = constants.fixPage.quickFixVersion;
const IMAGE_VERSION = constants.fixPage.defaultVersion;
const MAX_QUICK_IMAGE_SIZE = constants.fixPage.maxQuickFixVersion; // 4 MB
const MAX_IMAGE_SIZE = constants.fixPage.maxImageSize; // 8 MB
const PLACEHOLDER_IMAGE = constants.fixPage.placeHolderImage;
const NS = constants.NS;

const isFlippedVertical = (className) => className.indexOf(INKIVE.C.FLIP_IMAGE.VERTICAL) > -1;
const isFlippedHorizontal = (className) => className.indexOf(INKIVE.C.FLIP_IMAGE.HORIZONTAL) > -1;
const isFlipped = (className) => isFlippedVertical(className) || isFlippedHorizontal(className);
const getHref = (img) => img ? img.getAttribute('xlink:href') || img.getAttribute('href') || '' : '';
const setHref = (img, href) => {
  if (!img) return;

  if (img.hasAttribute('href')) img.setAttribute('href', href);
  else img.setAttributeNS(NS.XLINK, 'xlink:href', href);
};

export async function fixPageSvg (svg, pageId, quickMode = false) {
  await addFontDataUrls(svg, quickMode);
  await convertImagesToDataUrls(svg, pageId, quickMode);
  sanitizeIgnoredTagsSvg(svg);
  return svg;
}

async function getBase64Font (cssText) {
  const fontUrlRegex = /url\(([^()]*)\)/g;
  const matches = cssText.matchAll(fontUrlRegex);

  if (!matches) return cssText;

  let cssTextBase64 = cssText;
  for (const match of matches) {
    if (match.length === 2) {
      const fontUrl = match[1];
      const dataUrl = await getDataURL(fontUrl);
      cssTextBase64 = cssTextBase64.replace(fontUrl, dataUrl);
    }
  }

  return cssTextBase64;
}

function getTexts (svg) {
  const texts = [...svg.querySelectorAll('.textarea')];
  return texts.map(({ id }) => INKIVE.DesignFacade.get(svg.getElementById(id)))
    .filter((element) => element && element.font && !texts.find(({ family }) => family === element.font.family))
    .map(element => element.font);
}

async function createFontDefs (svg, quickMode) {
  const style = document.createElement('style');
  const defs = document.createElement('defs');

  // Load fonts in SVG
  if (!quickMode) {
    const texts = getTexts(svg);
    const forceAdd = true;
    await INKIVE.FontsFactory.importDesignCSSFonts(texts, forceAdd);
  }

  const desygnerFontSources = [...document.querySelectorAll('.desygner-font-source')]
    .map((i) => i.href)
    .concat([(new URL('/assets/css/fonts/nunito.css', document.baseURI)).href]);

  const fontTags = [...document.querySelectorAll('[id^=font-source]')];
  const fontSources = fontTags.reduce((fonts, font) => fonts + font.innerHTML, '');

  if (fontSources) {
    try {
      const base64CSS = await getBase64Font(fontSources);
      style.innerHTML += base64CSS;
    } catch (error) {
      logError('createFontDefs:fontSources')(error);
    }
  }

  for (const fs of desygnerFontSources) {
    try {
      const response = await fetch(fs);
      const cssText = await response.text();
      const base64CSS = await getBase64Font(cssText);
      style.innerHTML += base64CSS;
    } catch (error) {
      logError('createFontDefs:desygnerFontSources')(error);
    }
  }

  defs.appendChild(style);

  return defs;
}

export async function addFontDataUrls (svg, quickMode) {
  const cssDefs = await createFontDefs(svg, quickMode);
  svg.appendChild(cssDefs);
}

async function setImageDataUrl (img, url, svg) {
  const className = img.className.baseVal;

  if (isFlipped(className)) {
    return await setFlippedImageDataUrl();
  }

  // Images can be very large, and likely change often, don't try and cache them
  const doNotCache = true;
  const dataUrl = isDataURL(url) ? url : await getDataURL(url, doNotCache);
  return setHref(img, dataUrl);

  async function setFlippedImageDataUrl () {
    return await new Promise(resolve => {
      INKIVE.Tool.getInstance().editor.canvas.setFlipImage(
        img.id,
        { vertical: isFlippedVertical(className), horizontal: isFlippedHorizontal(className) },
        url,
        svg,
        resolve
      );
    });
  }
}

export async function convertImagesToDataUrls (svg, pageId, quickMode) {
  const images = getImages(svg);
  const validUrls = await getValidUrls(images, pageId, quickMode);

  return await Promise.all(validUrls.map((validUrl, i) => {
    return setImageDataUrl(images[i].img, validUrl, svg);
  }));
}

export function sanitizeIgnoredTagsSvg (svg) {
  return svg.querySelectorAll('[data-sanitize="ignore"]')
    .forEach(element => element.remove());
}

export function sanitizeHoverOutlineSvg (svg) {
  return svg.querySelectorAll('.hover-outlined')
    .forEach(element => element.remove());
}

export function sanitizeIgnoredSaveTagsSvg (svg) {
  return svg.querySelectorAll('[data-sanitize="ignore-save"]')
    .forEach(element => element.remove());
}

export function getImages (svg) {
  return [...svg.querySelectorAll('image')].map((img) => {
    const href = getHref(img);
    return !isDataURL(href) ? { img, href } : false;
  }).filter(Boolean);
}

function getImageVersions (url, quickMode) {
  const getVersion = (version) => INKIVE.ImagesFactory.getVersionUrl(url, version, true);

  return [
    getVersion(quickMode ? QUICK_IMAGE_VERSION : IMAGE_VERSION),
    getVersion('original'),
    getVersion(false)
  ];
}

async function isValidResource (url, quickMode) {
  let response;
  try {
    response = await fetch(url, { method: 'HEAD' });
    if (!response.ok) throw new Error('Forbidden');
  } catch (error) {
    return false;
  }

  const contentLength = response.headers.get('content-length');
  const maxAllowedImageSize = quickMode ? MAX_QUICK_IMAGE_SIZE : MAX_IMAGE_SIZE;
  return contentLength <= maxAllowedImageSize;
}

async function getValidVersion (versions, quickMode, i = 0) {
  const url = versions[i];
  const validResource = await isValidResource(url, quickMode);
  if (validResource) return url;
  else if (i < versions.length - 1) return await getValidVersion(versions, quickMode, i + 1);
  else return PLACEHOLDER_IMAGE;
}

export async function getValidUrls (images, pageId, quickMode) {
  const isActivePage = INKIVE.Tool._current_design_id === pageId;

  return await Promise.all(
    images.map(async ({ img, href }) => {
      const imgFromActivePage = document.querySelector(`image#${img.id}`);
      const urlFromActivePage = getHref(imgFromActivePage);

      const url = isActivePage
        ? urlFromActivePage
        : (href || getHref(img));

      if (isDataURL(url)) return url;

      const versions = getImageVersions(url, quickMode);
      const validVersion = await getValidVersion(versions, quickMode);

      return validVersion;
    })
  );
}
